import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useI18n } from '../../contexts/I18nContext';
import { 
  LayoutDashboard, Building2, FileText, User, Settings,
  TrendingUp, Wallet, LogOut, ChevronLeft, Menu, FileSignature, X
} from 'lucide-react';
import { Button } from '../ui/button';
import { useState, useEffect } from 'react';
import NotificationBell from '../common/NotificationBell';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_1c07591d-63e3-4991-946f-f8a8e8c125c9/artifacts/i94v1fmk_IMG_0910.jpeg";

const AppSidebar = () => {
  const { user, logout, isInvestor, isPropertyContributor } = useAuth();
  const { t } = useI18n();
  const location = useLocation();
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => { setMobileOpen(false); }, [location.pathname]);

  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') setMobileOpen(false); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  const isActive = (path) => location.pathname === path || location.pathname.startsWith(path + '/');

  const links = [
    { path: '/app/dashboard', label: t('nav.dashboard'), icon: LayoutDashboard },
    { path: '/app/opportunities', label: t('nav.opportunities'), icon: TrendingUp },
    { path: '/app/investments', label: t('nav.investments'), icon: Wallet },
    ...(isPropertyContributor ? [{ path: '/app/properties', label: t('nav.properties'), icon: Building2 }] : []),
    { path: '/app/documents', label: t('nav.documents'), icon: FileText },
    { path: '/app/signatures', label: 'Firmas', icon: FileSignature },
    { path: '/app/profile', label: t('nav.profile'), icon: User },
  ];

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const sidebarContent = (
    <div className="flex flex-col h-full">
      {/* Logo */}
      <div className="h-20 flex items-center justify-between px-4 border-b border-border">
        {!collapsed && (
          <Link to="/">
            <img src={LOGO_URL} alt="Silver Brick" className="h-10 w-auto" />
          </Link>
        )}
        <button
          onClick={() => setCollapsed(!collapsed)}
          className="hidden lg:block p-2 hover:bg-muted rounded-sm transition-colors"
          data-testid="sidebar-toggle"
        >
          {collapsed ? <Menu className="h-5 w-5" strokeWidth={1.5} /> : <ChevronLeft className="h-5 w-5" strokeWidth={1.5} />}
        </button>
        <button
          onClick={() => setMobileOpen(false)}
          className="lg:hidden p-2 hover:bg-muted rounded-sm transition-colors"
        >
          <X className="h-5 w-5" strokeWidth={1.5} />
        </button>
      </div>

      {/* User Info */}
      {!collapsed && (
        <div className="p-4 border-b border-border">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3 min-w-0">
              <div className="h-10 w-10 rounded-full bg-muted flex items-center justify-center flex-shrink-0">
                {user?.picture ? (
                  <img src={user.picture} alt="" className="h-10 w-10 rounded-full" />
                ) : (
                  <User className="h-5 w-5" strokeWidth={1.5} />
                )}
              </div>
              <div className="min-w-0">
                <p className="text-sm font-medium truncate">{user?.name}</p>
                <p className="text-xs text-muted-foreground capitalize">{user?.role?.replace('_', ' ')}</p>
              </div>
            </div>
            <NotificationBell />
          </div>
        </div>
      )}

      {/* Navigation */}
      <nav className="flex-1 p-2 space-y-1 overflow-y-auto">
        {links.map((link) => {
          const Icon = link.icon;
          return (
            <Link
              key={link.path}
              to={link.path}
              className={`flex items-center gap-3 px-3 py-3 rounded-sm transition-colors ${
                isActive(link.path)
                  ? 'bg-primary text-primary-foreground'
                  : 'text-muted-foreground hover:bg-muted hover:text-foreground'
              }`}
              title={collapsed ? link.label : undefined}
            >
              <Icon className="h-5 w-5 flex-shrink-0" strokeWidth={1.5} />
              {!collapsed && <span className="text-sm">{link.label}</span>}
            </Link>
          );
        })}
      </nav>

      {/* Logout */}
      <div className="p-2 border-t border-border">
        <Button
          variant="ghost"
          onClick={handleLogout}
          className={`w-full justify-start gap-3 text-muted-foreground hover:text-destructive ${collapsed ? 'px-3' : ''}`}
          data-testid="logout-btn"
        >
          <LogOut className="h-5 w-5" strokeWidth={1.5} />
          {!collapsed && <span className="text-sm">{t('nav.logout')}</span>}
        </Button>
      </div>
    </div>
  );

  return (
    <>
      {/* Mobile top bar */}
      <div className="lg:hidden fixed top-0 left-0 right-0 h-14 bg-card border-b border-border z-30 flex items-center px-4 gap-3">
        <button onClick={() => setMobileOpen(true)} className="p-2 hover:bg-muted rounded-sm transition-colors" data-testid="mobile-menu-toggle">
          <Menu className="h-5 w-5" strokeWidth={1.5} />
        </button>
        <Link to="/">
          <img src={LOGO_URL} alt="Silver Brick" className="h-8 w-auto" />
        </Link>
        <div className="ml-auto">
          <NotificationBell />
        </div>
      </div>

      {/* Mobile overlay */}
      {mobileOpen && (
        <div className="lg:hidden fixed inset-0 bg-black/60 z-40" onClick={() => setMobileOpen(false)} />
      )}

      {/* Mobile drawer */}
      <aside className={`lg:hidden fixed left-0 top-0 h-screen w-72 bg-card border-r border-border z-50 transform transition-transform duration-300 ${
        mobileOpen ? 'translate-x-0' : '-translate-x-full'
      }`}>
        {sidebarContent}
      </aside>

      {/* Desktop sidebar */}
      <aside className={`hidden lg:block fixed left-0 top-0 h-screen bg-card border-r border-border transition-all duration-300 z-40 ${
        collapsed ? 'w-16' : 'w-64'
      }`}>
        {sidebarContent}
      </aside>
    </>
  );
};

export default AppSidebar;
